/**
 * Where this browser is, for the changes that have to say so.
 *
 * Gordon: "I also want that the users/admins/contractors gps location are
 * logged with the tickets whenever there is a recorded change", and, asked
 * what should happen when a browser will not say, he chose to block the
 * change.
 *
 * ONE PLACE THAT ASKS, because the browser's permission prompt is a scarce
 * thing. A refusal sticks until somebody digs into site settings, so a badly
 * timed prompt does not cost one request - it costs the feature until that
 * person works out how to undo it. Nothing here asks on page load. The first
 * prompt a person sees is the one attached to a change they have just chosen
 * to make, which is the only moment the question makes sense.
 *
 * A FIX IS CACHED, for the same reason. Posting a comment, changing a status
 * and logging an hour within the same minute is three recorded changes, and
 * asking the device for three separate fixes means three GPS wake-ups and a
 * visible delay on each. One fix, reused while it is fresh, is both faster and
 * honest: it is where they were when they did all three.
 *
 * NEVER REJECTS. Every failure is a status - denied, unavailable, timeout,
 * insecure, unsupported - because the server has to be told WHY there is no
 * location, and "no header at all" is the one answer it cannot act on. A
 * caller that threw here would leave the request with nothing to say.
 */

/** How long a fix is good for. Long enough to cover a burst of edits. */
const FRESH_MS = 90 * 1000;

/** How long to wait for the device before giving up and saying so. */
const WAIT_MS = 8000;

let cached = null;      // { at, value }
let inflight = null;    // one request at a time, shared by every caller

/** Is the page allowed to ask at all? */
const secure = () => {
  try {
    return window.isSecureContext
      || window.location.hostname === 'localhost'
      || window.location.hostname === '127.0.0.1';
  } catch {
    return false;
  }
};

const reasonFor = (err) => {
  // The numeric codes, because the message is not stable across browsers.
  if (!err) return 'unavailable';
  if (err.code === 1) return 'denied';        // PERMISSION_DENIED
  if (err.code === 2) return 'unavailable';   // POSITION_UNAVAILABLE
  if (err.code === 3) return 'timeout';       // TIMEOUT
  return 'unavailable';
};

/**
 * A fix, or the reason there is not one.
 *
 * `force: true` skips the cache, for the "Try again" button somebody presses
 * after allowing the permission they had just refused - where reusing a
 * 'denied' from eight seconds ago would make the button look broken.
 */
export function whereAmI({ force = false } = {}) {
  if (!force && cached && Date.now() - cached.at < FRESH_MS) {
    return Promise.resolve(cached.value);
  }
  if (inflight) return inflight;

  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve({ status: 'unsupported' });
  }
  if (!secure()) {
    // Browsers will not even show the prompt on http, so asking produces a
    // silent nothing. Saying which it was is the difference between a fixable
    // deployment problem and a mystery.
    return Promise.resolve({ status: 'insecure' });
  }

  inflight = new Promise((resolve) => {
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      // A refusal is cached too, and deliberately: without it every single
      // change re-asks a device that has already said no, which is slow and
      // which some browsers treat as harassment.
      cached = { at: Date.now(), value };
      inflight = null;
      resolve(value);
    };

    navigator.geolocation.getCurrentPosition(
      (pos) => done({
        status: 'recorded',
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy
      }),
      (err) => done({ status: reasonFor(err) }),
      {
        // A rooftop-accurate fix can take half a minute indoors and nobody is
        // waiting that long to post a comment. High accuracy, a short leash,
        // and a recent cached fix from the browser's own store is accepted.
        enableHighAccuracy: true,
        timeout: WAIT_MS,
        maximumAge: FRESH_MS
      }
    );
  });
  return inflight;
}

/** Throw away what we know. For a "Try again" after fixing a permission. */
export function forget() {
  cached = null;
}

/** What we last found out, without asking. Null when nothing has been asked. */
export function known() {
  return cached ? cached.value : null;
}

export default whereAmI;
